import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { UserService } from '../user';
import { PracticeInfo, PracticeType } from '../practice/practice.model';
import { PracticeService } from '../practice';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Router } from '@angular/router';
import { LoadingService } from '../services/loading/loading.service';
import { AlertsService } from '../alerts/alerts.service';
import { FormControl } from '@angular/forms';
import { debounceTime } from 'rxjs/operators';
import { ModalService } from '../services/modal/modal.service';
import { AuthService } from '../authentication/auth.service';

@UntilDestroy()
@Component({
  selector: 'app-practice-select',
  templateUrl: './practice-select.component.html',
  styleUrls: ['./practice-select.component.scss']
})
export class PracticeSelectComponent implements OnInit {
  formControl = new FormControl('');
  @ViewChild('modalContent')
  modalContent!: TemplateRef<any>;
  private _practices: PracticeInfo[] = [];
  private _practice: PracticeInfo | null = null;
  private readonly _maxLength = 15;

  constructor(
    private readonly _router: Router,
    private readonly _userService: UserService,
    private readonly _practiceService: PracticeService,
    private readonly _loadingService: LoadingService,
    private readonly _alertService: AlertsService,
    private readonly _authService: AuthService,
    readonly modalService: ModalService
  ) {}

  async ngOnInit(): Promise<void> {
    await this.readPractices();
    await this.selectPractice(this.practices[0]);
    this.subscribe();
  }

  get practices() {
    return this._practices;
  }

  private set practices(practices: PracticeInfo[]) {
    this._practices = [...this._practices, ...practices];
  }

  get firstName() {
    return this.parseFirstName(this._practice, this._maxLength);
  }

  get lastName() {
    return this.parseLastName(this._practice, this._maxLength);
  }

  parseFirstName(practice: PracticeInfo | null, maxLength: number) {
    if (!practice) return '';
    const { name } = practice;
    if (name && name.length > maxLength) {
      const spaceCharacterIndex = this.findSpaceIndexUpTo(practice, maxLength);
      return spaceCharacterIndex ? name.slice(0, spaceCharacterIndex) : name;
    }
    return name;
  }

  parseLastName(practice: PracticeInfo | null, maxLength: number) {
    if (!practice) return '';
    const { name } = practice;
    if (name.length <= maxLength) return '';
    const spaceCharacterIndex = this.findSpaceIndexUpTo(practice, maxLength);
    if (!spaceCharacterIndex) return '';
    const lastName = name.slice(spaceCharacterIndex).slice(0, maxLength);
    return lastName ? lastName + '...' : '';
  }

  async selectPractice(practice: PracticeInfo) {
    if (!practice) return;
    this.modalService.close();
    this._loadingService.start();
    this._practiceService.clearPracticeInfo();
    this._practiceService.setPracticeInfo(practice);
    const { type } = practice;
    try {
      if (type === PracticeType.ENGAGEMENT)
        await this._practiceService.lazyLoadEngagementPractice();
      if (type === PracticeType.BUNDLED)
        await this._practiceService.loadBundledPractice();
    } catch (e) {
      this._alertService.showError(
        `Failed to load data for selected practice with ID ${practice.id}.`
      );
      console.log(e); // TODO: Replace with logging service when available.
    } finally {
      this._loadingService.stop();
      await this._router.navigate(['support'], {
        queryParams: { leaveMenuOpen: false }
      });
    }
  }

  async selectPracticeOnKeyDown(event: KeyboardEvent, practice: PracticeInfo) {
    event.preventDefault();
    const key = event.key;
    if (key !== 'Enter' && key !== ' ') {
      return;
    }
    await this.selectPractice(practice);
  }

  private async readAnalyticsPractices(keyword: string = ''): Promise<void> {
    try {
      const analyticsPractices =
        await this._userService.getAnalyticsUserPractices(keyword);
      this.practices = analyticsPractices.map((practice) => ({
        id: practice.practiceIdInt?.toString(),
        guid: practice.practiceId?.toString(),
        name: practice.displayName?.toString(),
        type: PracticeType.ANALYTICS
      }));
    } catch (e) {
      console.log(e); // TODO: Replace with logging service when available.
    }
  }

  private async readEngagementPracticesFromDentalGroup(
    keyword: string = ''
  ): Promise<void> {
    const user = this._userService.getUser();
    if (!user) return;
    const { dental_group: dentalGroup } = user;
    let practices = dentalGroup.map(({ id, guid, name }) => ({
      id: id?.toString(),
      guid: guid?.toString(),
      name: name?.toString(),
      type: PracticeType.ENGAGEMENT
    }));
    if (keyword) {
      const lowerKeyword = keyword.toLowerCase();
      practices = practices.filter(
        ({ id, guid, name }) =>
          id?.toLowerCase().includes(lowerKeyword) ||
          guid?.toLowerCase().includes(lowerKeyword) ||
          name?.toLowerCase().includes(lowerKeyword)
      );
    }
    this.practices = practices;
  }

  private async readDefaultEngagementPracticeGuid() {
    if (this._practiceService.getPracticeGuid()) return;
    const user = this._userService.getUser();
    if (!user) return;
    const deviceUuid = this._userService.getDeviceUuid(user.email);
    const userCredential = this._userService.getFirebaseUserCredential();
    const { practice_guid: practiceGuid } =
      await this._authService.hasAccessToEngagement(
        await userCredential.user.getIdToken(),
        deviceUuid
      );
    this._practiceService.setPracticeGuid(practiceGuid);
  }

  private async readDefaultEngagementPractice(keyword: string = '') {
    try {
      this._loadingService.start();
      await this.readDefaultEngagementPracticeGuid();
      await this._practiceService.lazyLoadEngagementPractice();
    } catch (e) {
      this._alertService.showError(
        `Failed to load default Engagement practice for authenticated user.`
      );
      console.log(e); // TODO: Replace with logging service when available.
    } finally {
      this._loadingService.stop();
    }
    const engagementPracticeGuid = this._practiceService.getPracticeGuid();
    const engagementPractice = this._practiceService.getPractice();
    if (!engagementPractice) return;
    const { id, guid, name } = engagementPractice;
    if (engagementPracticeGuid !== guid) return;
    if (
      !keyword ||
      id?.toString().toLowerCase().includes(keyword.toLowerCase()) ||
      guid?.toString().toLowerCase().includes(keyword.toLowerCase()) ||
      name?.toString().toLowerCase().includes(keyword.toLowerCase())
    ) {
      this.practices = [{ id, guid, name, type: PracticeType.ENGAGEMENT }];
    }
  }

  private async readEngagementPractices(keyword: string = '') {
    try {
      this._userService.getUser().dental_group?.length
        ? await this.readEngagementPracticesFromDentalGroup(keyword)
        : await this.readDefaultEngagementPractice(keyword);
    } catch (e) {
      console.log(e); // TODO: Replace with logging service when available.
    }
  }

  private bundlePractices(): void {
    // Split practices into Analytics and Engagement
    const analyticsPractices = this.practices.filter(
      (practice) => practice.type === PracticeType.ANALYTICS
    );
    const engagementPractices = this.practices.filter(
      (practice) => practice.type === PracticeType.ENGAGEMENT
    );
    // Find name duplicates between Analytics and Engagement
    const analyticsNames = new Set(
      analyticsPractices.map((practice) => this.parseFirstName(practice, 15))
    );
    const engagementNames = new Set(
      engagementPractices.map((practice) => this.parseFirstName(practice, 15))
    );
    const duplicateNames = [...engagementNames].filter((name) =>
      analyticsNames.has(name)
    );
    // Remove duplicates from practices
    this._practices = this._practices.filter(
      (practice) => !duplicateNames.includes(this.parseFirstName(practice, 15))
    );
    // Create bundled practices and append to practices
    this.practices = duplicateNames.map((name) => {
      const analyticsPractice = analyticsPractices.find(
        (practice) => this.parseFirstName(practice, 15) === name
      );
      const engagementPractice = engagementPractices.find(
        (practice) => this.parseFirstName(practice, 15) === name
      );
      return {
        id: analyticsPractice.id,
        engagementId: engagementPractice.id,
        guid: engagementPractice.guid,
        analyticsGuid: analyticsPractice.guid,
        name: analyticsPractice.name,
        type: PracticeType.BUNDLED
      };
    });
  }

  private async readPractices(keyword: string = ''): Promise<void> {
    this._loadingService.start();
    try {
      await this.readAnalyticsPractices(keyword);
      await this.readEngagementPractices(keyword);
      this.bundlePractices();
    } catch (e) {
      this._alertService.showError(
        'Something went wrong while loading practices. Please try again later.'
      );
      console.log(e); // TODO: Replace with logging service when available.
    } finally {
      this._loadingService.stop();
    }
  }

  private async search(keyword: string) {
    this._practices = [];
    await this.readPractices(keyword);
  }

  private findSpaceIndexUpTo(
    practice: PracticeInfo,
    maxLength: number
  ): number {
    const indices = [];
    const { name } = practice;
    for (let i = 0; i < name.length; i++) {
      if (name[i] === ' ') {
        indices.push(i);
      }
    }
    const reversedIndices = [...indices].reverse();
    return reversedIndices.find((spaceIndex) => spaceIndex < maxLength);
  }

  private subscribe() {
    this._practiceService.practiceState$
      .pipe(untilDestroyed(this))
      .subscribe((practice) => {
        this._practice = practice;
      });
    this.formControl.valueChanges
      .pipe(untilDestroyed(this))
      .pipe(debounceTime(500))
      .subscribe(async (value) => {
        await this.search(value);
      });
  }
}
